import {
	ChangeDetectionStrategy,
	Component,
	inject,
	OnInit,
	signal,
} from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { ActivatedRoute, Router, RouterModule } from "@angular/router";
import { SurveyStore } from "@app/stores/survey.store";
import { UserStore } from "@app/stores/user.store";
import { TranslateModule } from "@ngx-translate/core";
import { map } from "rxjs";
import { ApiService } from "src/services/api.service";
import { UserService } from "src/services/user.service";
import { NavbarComponent } from "../../../layout/components/navbar/navbar.component";

@Component({
	selector: "app-wpa-introduction",
	templateUrl: "./wpa-introduction.component.html",
	styleUrls: ["./wpa-introduction.component.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [RouterModule, TranslateModule, NavbarComponent],
	standalone: true,
})
export class WpaIntroductionComponent implements OnInit {
	readonly #userStore = inject(UserStore);
	readonly #userService = inject(UserService);
	readonly #router = inject(Router);
	readonly #route = inject(ActivatedRoute);
	readonly #apiService = inject(ApiService);
	readonly #surveyStore = inject(SurveyStore);

	public readonly surveyId = signal(this.#route.snapshot.params.surveyId);
	public readonly hasBothApvAndScreening = signal(false);
	public readonly isLoading = signal(true);

	public readonly apvOnly = toSignal(
		this.#route.queryParamMap.pipe(
			map(
				(params) =>
					params.get("apvOnly") === "true" || params.get("wpaOnly") === "true",
			),
		),
		{ initialValue: false },
	);

	public hasDoneAnything = signal(this.#userService.user?.HasCompletedSurvey);

	public ngOnInit(): void {
		this.#userStore.setSwaWpaIntroduction();
		// If apvOnly/wpaOnly query param is present, set APV-only mode
		// Also check store state as fallback (set from lang component)
		if (this.apvOnly() || this.#surveyStore.selectedOnlyApv()) {
			this.#surveyStore.setSelectedOnlyApv(true);
		}
		this.loadSurveyData();
	}

	private async loadSurveyData(): Promise<void> {
		const surveyIdValue = this.surveyId();
		if (surveyIdValue) {
			try {
				const survey = await this.#apiService.GetSurvey(
					surveyIdValue,
					"False",
					false,
				);
				this.hasBothApvAndScreening.set(
					survey?.IncludesAPV && survey?.IncludesScreening,
				);
			} catch (error) {
				console.error("Error loading survey data:", error);
			}
		}
		this.isLoading.set(false);
	}

	public handleWpaAndHs(): void {
		this.#surveyStore.setSelectedOnlyApv(false);
		this.#router.navigate(["survey", this.surveyId(), "introduction"], {
			queryParams: { fromWpa: "true" },
		});
	}

	public handleWpaOnly(): void {
		this.#surveyStore.setSelectedOnlyApv(true);
		this.#router.navigate(["survey", this.surveyId(), "introduction"], {
			queryParams: { fromWpa: "true" },
		});
	}

	public handleStart(): void {
		// If apvOnly is set, ensure selectedOnlyApv is true
		if (this.apvOnly()) {
			this.#surveyStore.setSelectedOnlyApv(true);
		}
		this.#router.navigate(["survey", this.surveyId(), "introduction"], {
			queryParams: { fromWpa: "true" },
		});
	}

	public back = (): void => {
		this.#router.navigate(["home"]);
	};
}
